import { Component } from '@theme/component';

/**
 * The back-in-stock ("Notify me") form inside a theme-drawer
 * (snippets/notify-me-drawer.liquid).
 *
 * The shopper picks one of the product's sold-out variants and leaves an email;
 * on submit the component registers the subscription with Klaviyo directly from
 * the browser. Two calls, in this order:
 *
 * 1. `POST /client/back-in-stock-subscriptions` — the subscription itself. This
 *    is the one that matters: it is awaited, and a failure is what the shopper
 *    is told about.
 * 2. `POST /client/profiles` — enriches the profile Klaviyo just created with
 *    the shopper's language and the source of the signup. Fire-and-forget: the
 *    subscription is already registered without it, and the shopper has nothing
 *    to do about a failure. `locale` is what the back-in-stock flow reads to
 *    pick the language the notification email goes out in, so on a catalog sold
 *    in five languages this is not optional dressing.
 *
 * Neither endpoint sets marketing consent — the legacy theme used a third call
 * (`/client/subscriptions`) for that, driven by a checkbox this drawer does not
 * have. Nothing here subscribes anyone to the newsletter.
 *
 * Both are Klaviyo's public "client" endpoints: public-key auth, no secret in
 * the page, CORS open to the storefront. That also means they are writable by
 * anyone who reads the company id out of the page — the same exposure the
 * Klaviyo onsite embed already carries, and the reason nothing but an email and
 * a catalog variant id is ever sent.
 *
 * Validation is native (`required`, `type=email`) surfaced through the theme's
 * `aria-invalid` hook: base.css paints the error state, this component only
 * toggles the attribute and the form-level error line.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLSelectElement} variantSelect - The sold-out variant picker.
 * @property {HTMLInputElement} email - The email field.
 * @property {HTMLElement} [formError] - Form-level error line.
 * @property {HTMLElement} submitLabel - The submit button's resting label.
 * @property {HTMLElement} submitSuccess - The confirmation the submit button turns into.
 *
 * @extends {Component<Refs>}
 */
class NotifyMeFormComponent extends Component {
  requiredRefs = ['form', 'submitButton', 'variantSelect', 'email', 'submitLabel', 'submitSuccess'];

  /**
   * Klaviyo pins request/response shapes to a dated API revision. This is the
   * one the legacy theme ships against in production — moving it is an API
   * migration, not a version bump.
   */
  static #revision = '2026-04-15';

  /**
   * Klaviyo addresses Shopify variants in its catalog by a composed id:
   * integration, catalog, then the Shopify variant id.
   *
   * @param {string} variantId
   * @returns {string}
   */
  static #catalogVariantId = (variantId) => `$shopify:::$default:::${variantId}`;

  /** The Liquid-rendered validation copy, restored after a submit error overwrote it. */
  #validationMessage = '';

  /** The Liquid-rendered CTA label, restored after the busy label overwrote it. */
  #restingLabel = '';

  connectedCallback() {
    super.connectedCallback();

    this.#validationMessage = this.refs.formError?.textContent ?? '';
    this.#restingLabel = this.refs.submitLabel.textContent ?? '';
    // Suppress native bubbles only when this component is live to drive the
    // aria-invalid error styling itself; without JS the browser still blocks
    // invalid submits natively.
    this.refs.form.noValidate = true;

    if (!this.#companyId) {
      // Surface missing configuration at load rather than as what looks like a
      // Klaviyo outage on the first submit — the merchant previewing the page
      // sees this in the console before a shopper ever hits it.
      console.warn(
        'Notify me drawer: no Klaviyo company id. Set the public API key in Theme settings → Klaviyo, or enable the Klaviyo onsite app embed.',
      );
    }
  }

  /**
   * Points the drawer at one variant and puts the form back to its resting
   * state, so a shopper who already registered one size sees a live CTA rather
   * than the confirmation left on it from last time.
   *
   * Called by the "Notify me" CTA before it opens the drawer
   * (assets/product-form.js), which is the only thing that opens this drawer.
   *
   * @param {string} [variantId] - The variant the CTA is standing for.
   */
  prepare(variantId) {
    const { variantSelect } = this.refs;

    this.#resetSubmitButton();
    this.#clearErrors();

    // A variant that isn't in the list is one that can be bought — the CTA
    // shouldn't have opened this drawer for it. Checked rather than assigned
    // blind: assigning an absent value leaves the select on index -1, showing
    // an empty box where the shopper expects the size they picked.
    if (variantId && [...variantSelect.options].some((option) => option.value === variantId)) {
      variantSelect.value = variantId;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (!this.#validate()) return;

    const { submitButton, submitLabel, variantSelect, email } = this.refs;
    const busyLabel = submitButton.dataset.busyLabel;

    submitButton.disabled = true;
    if (busyLabel) submitLabel.textContent = busyLabel;

    try {
      await this.#subscribe(email.value.trim(), variantSelect.value);
      // Deliberately not awaited: the subscription above is what the shopper
      // asked for and it has already landed.
      this.#updateProfile(email.value.trim());
      this.#showSuccess();
    } catch (error) {
      console.error('[notify-me] Back in stock subscription failed:', error);
      this.#showError(this.dataset.submitError);
      this.#resetSubmitButton();
    }
  }

  /**
   * Takes the CTA out of its confirmation once the shopper edits the form again,
   * so a second size can be registered without closing the drawer. Without this
   * the button would sit disabled and confirming a subscription for the size the
   * shopper just changed away from.
   */
  handleFieldChange() {
    if (this.refs.submitSuccess.hidden) return;
    this.#resetSubmitButton();
  }

  /**
   * Registers the back-in-stock subscription. Awaited, and the only call whose
   * failure the shopper hears about.
   *
   * @param {string} email
   * @param {string} variantId
   */
  async #subscribe(email, variantId) {
    const url = this.#clientUrl('back-in-stock-subscriptions');
    if (!url || !variantId) throw new Error('Missing back in stock configuration');

    const response = await fetch(url, {
      method: 'POST',
      headers: NotifyMeFormComponent.#headers,
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: NotifyMeFormComponent.#catalogVariantId(variantId),
              },
            },
          },
        },
      }),
      keepalive: true,
    });

    if (!response.ok) throw new Error(`Back in stock subscription failed: ${response.status}`);
  }

  /**
   * Adds the shopper's language and the signup source to the profile the
   * subscription just created or matched.
   *
   * `locale` is the one that earns its keep: the back-in-stock flow reads it to
   * decide which language template the notification email goes out in, and
   * without it every shopper gets the flow's default language. The two
   * properties are segmentation data, carried over from the legacy theme so
   * existing Klaviyo segments keep matching.
   *
   * @param {string} email
   */
  #updateProfile(email) {
    const url = this.#clientUrl('profiles');
    const locale = this.dataset.locale;
    if (!url || !locale) return;

    fetch(url, {
      method: 'POST',
      headers: NotifyMeFormComponent.#headers,
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: {
            email,
            locale,
            properties: {
              customer_language: locale,
              back_in_stock_source: 'pdp_back_in_stock',
            },
          },
        },
      }),
      keepalive: true,
    }).catch((error) => {
      // The subscription is registered either way, so this never reaches the
      // shopper — it costs Klaviyo the language and the attribution, nothing else.
      console.warn('[notify-me] Profile enrichment failed:', error);
    });
  }

  static get #headers() {
    return {
      accept: 'application/vnd.api+json',
      'content-type': 'application/vnd.api+json',
      revision: NotifyMeFormComponent.#revision,
    };
  }

  /**
   * @param {string} path
   * @returns {string} The client endpoint, or '' when the company id is unknown.
   */
  #clientUrl(path) {
    const companyId = this.#companyId;
    if (!companyId) return '';
    return `https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`;
  }

  /**
   * The Klaviyo public API key, which doubles as the company id.
   *
   * Prefers the theme setting, and falls back to reading it back off the
   * Klaviyo onsite embed's own script tag — so a store running the app embed
   * works without anyone filling the setting in, and a store that isn't can
   * still drive this drawer from the setting alone.
   *
   * @returns {string}
   */
  get #companyId() {
    if (this.dataset.companyId) return this.dataset.companyId;

    for (const script of document.scripts) {
      if (!script.src.includes('klaviyo')) continue;
      try {
        const companyId = new URL(script.src, window.location.href).searchParams.get('company_id');
        if (companyId) return companyId;
      } catch {
        // A src that isn't a resolvable URL tells us nothing; keep looking.
      }
    }

    return '';
  }

  /**
   * @returns {boolean} Whether the form is valid, painting the invalid fields if not.
   */
  #validate() {
    this.#clearErrors();

    const invalid = [...this.refs.form.elements].filter(
      /** @param {Element} element */
      (element) =>
        (element instanceof HTMLInputElement || element instanceof HTMLSelectElement) && !element.checkValidity(),
    );

    if (invalid.length === 0) return true;

    for (const field of invalid) {
      field.setAttribute('aria-invalid', 'true');
      if (this.refs.formError?.id) field.setAttribute('aria-describedby', this.refs.formError.id);
    }

    this.#showError(this.#validationMessage);
    /** @type {HTMLElement} */ (invalid[0]).focus();

    return false;
  }

  /** Clears the field-level and form-level error state. */
  #clearErrors() {
    for (const field of this.refs.form.querySelectorAll('[aria-invalid]')) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    const { formError } = this.refs;
    if (formError) {
      formError.hidden = true;
      formError.textContent = this.#validationMessage;
    }
  }

  /** @param {string} [message] */
  #showError(message) {
    const { formError } = this.refs;
    if (!formError || !message) return;

    formError.textContent = message;
    formError.hidden = false;
  }

  /**
   * Turns the CTA into the confirmation: the fields stay as they are, so the
   * shopper can see what they just registered for and change it if they meant
   * another size.
   */
  #showSuccess() {
    const { submitButton, submitLabel, submitSuccess } = this.refs;

    submitLabel.hidden = true;
    submitSuccess.hidden = false;
    // Nothing left to submit until something changes — `handleFieldChange` is
    // what gives the button back.
    submitButton.disabled = true;
  }

  /** Returns the CTA to its resting label, out of the busy or confirmed state. */
  #resetSubmitButton() {
    const { submitButton, submitLabel, submitSuccess } = this.refs;

    submitSuccess.hidden = true;
    submitLabel.hidden = false;
    submitLabel.textContent = this.#restingLabel;
    submitButton.disabled = false;
  }
}

if (!customElements.get('notify-me-form-component')) {
  customElements.define('notify-me-form-component', NotifyMeFormComponent);
}
